import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { requestsApi, offersApi, patientsApi, Patient, Offer, OfferTransportMode } from '../../services/api';
import RequestContextBar from '../../components/RequestContextBar/RequestContextBar';
// ShortlistSidebar removed — shortlist info is shown in the toolbar
import PatientSelectionStep from './PatientSelectionStep/PatientSelectionStep';
import OfferConfigurationStep from './OfferConfigurationStep/OfferConfigurationStep';
import LeaveConfirmDialog from '../../components/LeaveConfirmDialog/LeaveConfirmDialog';
import { useNavigationBlock, bypassNavigationBlock } from '../../hooks/useNavigationBlock';
import './OfferCreate.css';

type Step = 'selection' | 'configuration';
type TransportIntent = 'full' | 'ext_transport' | 'treatment_only' | 'provide_transport';

// Locked patients from other offers (shown but not editable in new-offer mode)
export interface LockedPatientInfo {
  offerIndex: number; // 1-based offer number
  offerId: string;
}

function OfferCreate() {
  const { t } = useTranslation();
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState<any>(null);
  const [error, setError] = useState('');

  const [currentStep, setCurrentStep] = useState<Step>('selection');
  const [shortlistedPatients, setShortlistedPatients] = useState<Patient[]>([]);
  const [selectedOfferType, setSelectedOfferType] = useState<{ [patientId: string]: 'TREATMENT_AND_TRANSPORT' | 'TREATMENT_ONLY' }>({});
  const [existingOfferId, setExistingOfferId] = useState<string | undefined>();
  const [existingReceivingStartPointId, setExistingReceivingStartPointId] = useState<string | undefined>();
  const [existingComment, setExistingComment] = useState<string | undefined>();
  const [existingTransportMode, setExistingTransportMode] = useState<OfferTransportMode | undefined>();
  const [existingTransportProviderNationId, setExistingTransportProviderNationId] = useState<string | undefined>();
  const [existingTransportAgreementNote, setExistingTransportAgreementNote] = useState<string | undefined>();
  const [transportIntent, setTransportIntent] = useState<{ [patientId: string]: TransportIntent }>({});
  const [showHelp, setShowHelp] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [showDraftDialog, setShowDraftDialog] = useState(false);
  const [pendingDraft, setPendingDraft] = useState<any>(null);
  const draftChecked = useRef(false);
  const isInitialized = useRef(false);
  const pendingProceedRef = useRef<(() => void) | null>(null);
  const [isDirty, setIsDirty] = useState(false);

  // Block navigation when there are unsaved changes
  useNavigationBlock(isDirty && !showLeaveDialog, useCallback((proceed: () => void) => {
    pendingProceedRef.current = proceed;
    setShowLeaveDialog(true);
  }, []));

  // Multi-offer state
  const [existingOffers, setExistingOffers] = useState<Offer[]>([]);
  const [showOfferSelectDialog, setShowOfferSelectDialog] = useState(false);
  const [lockedPatients, setLockedPatients] = useState<Map<string, LockedPatientInfo>>(new Map());

  // Nation-aware draft key to prevent cross-nation data leak (EXCON/SIM)
  const getDraftKey = useCallback(() => {
    const nation = user?.nation || 'unknown';
    return `offer-draft-${requestId}-${nation}`;
  }, [requestId, user?.nation]);

  // Track initial state to detect real changes
  const initialStateRef = useRef<{
    patientIds: string[];
    offerTypes: { [patientId: string]: string };
  }>({ patientIds: [], offerTypes: {} });

  // Load request details
  useEffect(() => {
    loadRequest();
  }, [requestId]);

  // Auto-save to localStorage — only after initialization and only for real changes
  useEffect(() => {
    if (!isInitialized.current) return;
    if (!requestId || shortlistedPatients.length === 0) return;

    setIsDirty(true);
    const draftKey = getDraftKey();
    const draft = {
      shortlistedPatients,
      selectedOfferType,
      currentStep,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(draftKey, JSON.stringify(draft));
  }, [shortlistedPatients, selectedOfferType, currentStep, requestId, getDraftKey]);

  // Load draft on mount (ref guard prevents StrictMode double-fire)
  // Only prompt for draft if there is no existing offer (existing offer takes priority)
  useEffect(() => {
    if (draftChecked.current) return;
    draftChecked.current = true;

    if (requestId && !existingOfferId) {
      const draftKey = getDraftKey();
      const savedDraft = localStorage.getItem(draftKey);
      if (savedDraft) {
        try {
          const draft = JSON.parse(savedDraft);
          // Show custom dialog instead of window.confirm
          setPendingDraft(draft);
          setShowDraftDialog(true);
        } catch (err) {
          console.error('Failed to load draft:', err);
        }
      }
    }
  }, [requestId, existingOfferId, getDraftKey]);

  const handleDraftResume = () => {
    if (pendingDraft) {
      const draftPatients = pendingDraft.shortlistedPatients || [];
      const draftOfferTypes = pendingDraft.selectedOfferType || {};
      setShortlistedPatients(draftPatients);
      setSelectedOfferType(draftOfferTypes);
      setCurrentStep(pendingDraft.currentStep || 'selection');
      // Snapshot initial state for change detection
      initialStateRef.current = {
        patientIds: draftPatients.map((p: Patient) => p.id).sort(),
        offerTypes: { ...draftOfferTypes }
      };
      isInitialized.current = true;
    }
    setShowDraftDialog(false);
    setPendingDraft(null);
  };

  const handleDraftDiscard = () => {
    localStorage.removeItem(getDraftKey());
    isInitialized.current = true;
    setShowDraftDialog(false);
    setPendingDraft(null);
  };

  const handleDraftCancel = () => {
    setShowDraftDialog(false);
    setPendingDraft(null);
    navigate(`/requests/${requestId}`);
  };

  const loadRequest = async () => {
    if (!requestId) return;

    try {
      setLoading(true);
      const response = await requestsApi.getById(requestId);
      setRequest(response.data);

      // Check if nation already has offers for this request
      await loadExistingOffers(requestId);
    } catch (err: any) {
      setError(err.response?.data?.error || t('common.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const loadExistingOffers = async (reqId: string) => {
    try {
      const offerResponse = await offersApi.getMyOffers(reqId);
      const offers = offerResponse.data;

      if (offers.length > 0) {
        setExistingOffers(offers);
        // Show selection dialog: edit existing or create new
        setShowOfferSelectDialog(true);
      } else {
        // No existing offers — fresh wizard
        isInitialized.current = true;
      }
    } catch (err) {
      console.error('Failed to load existing offers:', err);
      isInitialized.current = true;
    }
  };

  const populateFromOffer = async (offer: Offer) => {
    setExistingOfferId(offer.id);
    setExistingReceivingStartPointId(offer.receivingStartPointId || undefined);
    setExistingComment(offer.comment || undefined);
    setExistingTransportMode(offer.transportMode || undefined);
    setExistingTransportProviderNationId(offer.transportProviderNationId || undefined);
    setExistingTransportAgreementNote(offer.transportAgreementNote || undefined);

    // Fetch full patient data for the offered patients
    const patientIds = offer.offerPatients.map(op => op.patientId);
    const patientsResponse = await patientsApi.getByRequestId(requestId!, { limit: 1000 });
    const allPatients = patientsResponse.data.patients;
    const offeredPatients = allPatients.filter(p => patientIds.includes(p.id));

    if (offeredPatients.length > 0) {
      setShortlistedPatients(offeredPatients);

      const mapOfferType = (t: string): 'TREATMENT_AND_TRANSPORT' | 'TREATMENT_ONLY' => {
        if (t === 'BOTH' || t === 'TREATMENT_AND_TRANSPORT') return 'TREATMENT_AND_TRANSPORT';
        return 'TREATMENT_ONLY';
      };
      const offerTypes: { [patientId: string]: 'TREATMENT_AND_TRANSPORT' | 'TREATMENT_ONLY' } = {};
      const intents: { [patientId: string]: TransportIntent } = {};
      offer.offerPatients.forEach(op => {
        const opType = op.offerType || offer.offerType;
        if (opType === 'TRANSPORT_EXTENSION') {
          offerTypes[op.patientId] = 'TREATMENT_ONLY';
          intents[op.patientId] = 'provide_transport';
        } else {
          offerTypes[op.patientId] = mapOfferType(opType);
          if (mapOfferType(opType) === 'TREATMENT_AND_TRANSPORT') {
            intents[op.patientId] = 'full';
          } else if ((op as any).transportMode === 'PROVIDED_BY_PARTNER_NATION' || offer.transportMode === 'PROVIDED_BY_PARTNER_NATION') {
            intents[op.patientId] = 'ext_transport';
          } else {
            intents[op.patientId] = 'treatment_only';
          }
        }
      });
      setSelectedOfferType(offerTypes);
      setTransportIntent(intents);

      initialStateRef.current = {
        patientIds: offeredPatients.map(p => p.id).sort(),
        offerTypes: { ...offerTypes }
      };
    }
    isInitialized.current = true;
  };

  const handleSelectEditOffer = (offer: Offer) => {
    setShowOfferSelectDialog(false);
    // Build locked patients from OTHER offers (not the one being edited)
    const locked = new Map<string, LockedPatientInfo>();
    existingOffers.forEach((o, idx) => {
      if (o.id === offer.id) return;
      o.offerPatients.forEach(op => {
        locked.set(op.patientId, { offerIndex: idx + 1, offerId: o.id });
      });
    });
    setLockedPatients(locked);
    populateFromOffer(offer);
  };

  const handleSelectCreateNew = () => {
    setShowOfferSelectDialog(false);
    // Lock ALL patients from ALL existing offers
    const locked = new Map<string, LockedPatientInfo>();
    existingOffers.forEach((o, idx) => {
      o.offerPatients.forEach(op => {
        locked.set(op.patientId, { offerIndex: idx + 1, offerId: o.id });
      });
    });
    setLockedPatients(locked);
    isInitialized.current = true;
  };

  const handleOfferSelectCancel = () => {
    setShowOfferSelectDialog(false);
    navigate(`/requests/${requestId}`);
  };

  const isTransportRequest = request?.category === 'TRANSPORT';

  const handleAddToShortlist = (patients: Patient[], offerType: 'TREATMENT_AND_TRANSPORT' | 'TREATMENT_ONLY') => {
    const newPatients = patients.filter(
      p => !shortlistedPatients.some(sp => sp.id === p.id) && !lockedPatients.has(p.id)
    );

    setShortlistedPatients(prev => [...prev, ...newPatients]);

    const newOfferTypes = { ...selectedOfferType };
    const newIntents = { ...transportIntent };
    newPatients.forEach(p => {
      if (isTransportRequest) {
        // Transport-only requests: force provide_transport intent
        newOfferTypes[p.id] = 'TREATMENT_ONLY';
        newIntents[p.id] = 'provide_transport';
      } else if (!newOfferTypes[p.id]) {
        newOfferTypes[p.id] = offerType;
      }
    });
    setSelectedOfferType(newOfferTypes);
    if (isTransportRequest) setTransportIntent(newIntents);
  };

  const handleRemoveFromShortlist = (patientId: string) => {
    setShortlistedPatients(prev => prev.filter(p => p.id !== patientId));
    setSelectedOfferType(prev => {
      const updated = { ...prev };
      delete updated[patientId];
      return updated;
    });
  };

  const handleClearShortlist = () => {
    if (window.confirm(`${t('common.clear')} ${shortlistedPatients.length} ${t('requests.patients').toLowerCase()}?`)) {
      setShortlistedPatients([]);
      setSelectedOfferType({});
    }
  };

  const handleChangeOfferType = (patientId: string, offerType: 'TREATMENT_AND_TRANSPORT' | 'TREATMENT_ONLY') => {
    setSelectedOfferType(prev => ({
      ...prev,
      [patientId]: offerType
    }));
  };

  const handleSetTransportIntent = (patientId: string, intent: TransportIntent) => {
    setTransportIntent(prev => ({ ...prev, [patientId]: intent }));
  };

  const handleContinueToConfiguration = () => {
    if (shortlistedPatients.length === 0) {
      alert('Please add at least one patient to the shortlist before continuing.');
      return;
    }
    setCurrentStep('configuration');
  };

  const handleBackToSelection = () => {
    setCurrentStep('selection');
  };

  const handleOfferSubmitted = () => {
    // Clear dirty state so navigation block doesn't trigger
    setIsDirty(false);
    // Clear draft
    localStorage.removeItem(getDraftKey());
    // Bypass navigation block immediately (setIsDirty may not have flushed yet due to React batching)
    bypassNavigationBlock();
    // Navigate back to request detail
    navigate(`/requests/${requestId}`);
  };

  const hasActualChanges = useCallback(() => {
    const initial = initialStateRef.current;
    const currentIds = shortlistedPatients.map(p => p.id).sort();

    // Different number of patients
    if (currentIds.length !== initial.patientIds.length) return true;

    // Different patient IDs
    if (currentIds.some((id, i) => id !== initial.patientIds[i])) return true;

    // Different offer types
    for (const id of currentIds) {
      if (selectedOfferType[id] !== initial.offerTypes[id]) return true;
    }

    return false;
  }, [shortlistedPatients, selectedOfferType]);

  const handleCancel = () => {
    if (hasActualChanges()) {
      setShowLeaveDialog(true);
      return;
    }
    // No changes — clean up draft if it exists
    localStorage.removeItem(getDraftKey());
    navigate(`/requests/${requestId}`);
  };

  const handleSaveAndLeave = () => {
    // Draft is already auto-saved via the useEffect
    setIsDirty(false);
    setShowLeaveDialog(false);
    if (pendingProceedRef.current) {
      pendingProceedRef.current();
      pendingProceedRef.current = null;
    } else {
      navigate(`/requests/${requestId}`);
    }
  };

  const handleDiscardAndLeave = () => {
    // Remove draft from localStorage
    localStorage.removeItem(getDraftKey());
    setIsDirty(false);
    setShowLeaveDialog(false);
    if (pendingProceedRef.current) {
      pendingProceedRef.current();
      pendingProceedRef.current = null;
    } else {
      navigate(`/requests/${requestId}`);
    }
  };

  const handleStay = () => {
    setShowLeaveDialog(false);
    pendingProceedRef.current = null;
  };

  if (loading) {
    return (
      <div className="offer-create-page">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !request) {
    return (
      <div className="offer-create-page">
        <div className="error-container">
          <h3>{t('common.error')}</h3>
          <p>{error || t('common.noResults')}</p>
          <button className="btn btn-primary" onClick={() => navigate('/requests')}>
            {t('requests.backToRequests')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="offer-create-page">
      <RequestContextBar requestId={requestId!} onExit={handleCancel} onHelp={() => setShowHelp(true)} />

      <div className="offer-create-container">
        <div className="main-content">
          {currentStep === 'selection' && (
            <PatientSelectionStep
              requestId={requestId!}
              requestCategory={request?.category || 'MEDEVAC'}
              shortlistedPatientIds={shortlistedPatients.map(p => p.id)}
              selectedOfferType={selectedOfferType}
              transportIntent={transportIntent}
              lockedPatients={lockedPatients}
              onAddToShortlist={handleAddToShortlist}
              onRemoveFromShortlist={handleRemoveFromShortlist}
              onChangeOfferType={handleChangeOfferType}
              onSetTransportIntent={handleSetTransportIntent}
              onContinue={handleContinueToConfiguration}
              onHelp={() => setShowHelp(true)}
            />
          )}

          {currentStep === 'configuration' && (
            <OfferConfigurationStep
              requestId={requestId!}
              requestCategory={request.category}
              requestExitPoint={request.exitPoint}
              shortlistedPatients={shortlistedPatients}
              selectedOfferType={selectedOfferType}
              transportIntent={transportIntent}
              existingOfferId={existingOfferId}
              existingReceivingStartPointId={existingReceivingStartPointId}
              existingComment={existingComment}
              existingTransportMode={existingTransportMode}
              existingTransportProviderNationId={existingTransportProviderNationId}
              existingTransportAgreementNote={existingTransportAgreementNote}
              onBack={handleBackToSelection}
              onSubmitted={handleOfferSubmitted}
            />
          )}
        </div>

      </div>

      {showLeaveDialog && (
        <LeaveConfirmDialog
          onSaveAndLeave={handleSaveAndLeave}
          onDiscardAndLeave={handleDiscardAndLeave}
          onStay={handleStay}
        />
      )}

      {showDraftDialog && pendingDraft && (
        <div className="leave-dialog-overlay">
          <div className="leave-dialog draft-resume-dialog">
            <h3>{t('requests.saveDraft')}?</h3>
            <p>
              A saved draft from <strong>{new Date(pendingDraft.timestamp).toLocaleString()}</strong> was
              found with <strong>{pendingDraft.shortlistedPatients?.length || 0} patient(s)</strong> shortlisted.
            </p>
            <div className="leave-dialog-actions">
              <button className="leave-btn leave-btn-save" onClick={handleDraftResume}>
                {t('requests.saveDraft')}
              </button>
              <button className="leave-btn leave-btn-discard" onClick={handleDraftDiscard}>
                {t('common.delete')}
              </button>
              <button className="leave-btn leave-btn-stay" onClick={handleDraftCancel}>
                {t('common.cancel')}
              </button>
            </div>
            <p className="draft-dialog-hint">
              "Discard" will permanently delete the saved draft. "Cancel" returns to the request.
            </p>
          </div>
        </div>
      )}

      {/* Offer Selection Dialog (when existing offers found) */}
      {showOfferSelectDialog && existingOffers.length > 0 && (
        <div className="leave-dialog-overlay">
          <div className="leave-dialog offer-select-dialog">
            <h3>{t('offers.title')}</h3>
            <p>
              You have <strong>{existingOffers.length} offer(s)</strong> for this request.
              Edit an existing offer or create a new one with different patients.
            </p>
            <div className="offer-select-list">
              {existingOffers.map((offer, idx) => (
                <div key={offer.id} className="offer-select-item" onClick={() => handleSelectEditOffer(offer)}>
                  <div className="offer-select-item-header">
                    <strong>Offer #{idx + 1}</strong>
                    <span className={`offer-select-status offer-status-${offer.status.toLowerCase()}`}>{offer.status}</span>
                  </div>
                  <div className="offer-select-item-info">
                    {offer.offerPatients.length} patient(s)
                    {offer.receivingStartPoint && <> &middot; {offer.receivingStartPoint.name}</>}
                  </div>
                </div>
              ))}
            </div>
            <div className="leave-dialog-actions">
              <button className="leave-btn leave-btn-save" onClick={handleSelectCreateNew}>
                {t('offers.createOffer')}
              </button>
              <button className="leave-btn leave-btn-stay" onClick={handleOfferSelectCancel}>
                {t('common.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help Modal */}
      {showHelp && (
        <div className="help-overlay" onClick={() => setShowHelp(false)}>
          <div className="help-modal" onClick={(e) => e.stopPropagation()}>
            <div className="help-modal-header">
              <h2>{t('common.info')}</h2>
              <button className="help-modal-close" onClick={() => setShowHelp(false)}><X size={18} /></button>
            </div>
            <div className="help-modal-body">
              <section className="help-section">
                <h3>Step 1: Select Patients</h3>
                <p>Select patients from the list to add them to your offer. Use the filters in the toolbar to narrow the patient list.</p>
                <ul>
                  <li><strong>Click</strong> a row to select a patient. Use <strong>Ctrl+Click</strong> to select multiple patients, or <strong>Shift+Click</strong> for range selection.</li>
                  <li><strong>Right-click</strong> on selected patients to open the context menu and choose the offer type.</li>
                  <li><strong>Add All Filtered</strong> adds all currently visible (filtered) patients at once.</li>
                </ul>
              </section>

              <section className="help-section">
                <h3>Context Menu: Offer Types</h3>
                <table className="help-icon-table">
                  <tbody>
                    <tr>
                      <td><strong>Full Offer</strong></td>
                      <td><img src="/icons/offer_combined_gruen.webp" alt="" width={42} height={24} /></td>
                      <td>Your nation provides both treatment and transport (Treatment &amp; Transport).</td>
                    </tr>
                    <tr>
                      <td><strong>Treatment + Ext. Transport</strong></td>
                      <td>
                        <span className="help-icon-combo">
                          <img src="/icons/delivery_gelb.webp" alt="" width={24} height={24} />
                          <img src="/icons/hospital_gruen.webp" alt="" width={24} height={24} />
                        </span>
                      </td>
                      <td>Your nation provides treatment. Transport is expected from a partner nation.</td>
                    </tr>
                    <tr>
                      <td><strong>Only Treatment</strong></td>
                      <td>
                        <span className="help-icon-combo">
                          <img src="/icons/delivery_leer.webp" alt="" width={24} height={24} />
                          <img src="/icons/hospital_gruen.webp" alt="" width={24} height={24} />
                        </span>
                      </td>
                      <td>Your nation provides treatment only. Transport is open / not yet arranged.</td>
                    </tr>
                    <tr>
                      <td><strong>Provide Transport</strong></td>
                      <td>
                        <span className="help-icon-combo">
                          <img src="/icons/delivery_gruen.webp" alt="" width={24} height={24} />
                          <img src="/icons/hospital_gelb.webp" alt="" width={24} height={24} />
                        </span>
                      </td>
                      <td>Your nation offers to provide transport for patients that other nations are treating. Only available when selected patients have treatment-only offers from other nations. A notification will be sent to the treatment nations.</td>
                    </tr>
                  </tbody>
                </table>
              </section>

              <section className="help-section">
                <h3>Offers Column Icons</h3>
                <table className="help-icon-table">
                  <tbody>
                    <tr>
                      <td><img src="/icons/offer_combined_gruen.webp" alt="" width={42} height={24} /></td>
                      <td>At least one nation has offered both treatment and transport for this patient.</td>
                    </tr>
                    <tr>
                      <td>
                        <span className="help-icon-combo">
                          <img src="/icons/delivery_gruen.webp" alt="" width={24} height={24} />
                          <img src="/icons/hospital_gruen.webp" alt="" width={24} height={24} />
                        </span>
                      </td>
                      <td>Separate offers exist for treatment and transport from different nations.</td>
                    </tr>
                    <tr>
                      <td>
                        <span className="help-icon-combo">
                          <img src="/icons/delivery_leer.webp" alt="" width={24} height={24} />
                          <img src="/icons/hospital_gruen.webp" alt="" width={24} height={24} />
                        </span>
                      </td>
                      <td>Treatment is offered, but no transport offer exists yet.</td>
                    </tr>
                    <tr>
                      <td>
                        <span className="help-icon-combo">
                          <img src="/icons/delivery_leer.webp" alt="" width={24} height={24} />
                          <img src="/icons/hospital_leer.webp" alt="" width={24} height={24} />
                        </span>
                      </td>
                      <td>No offers exist for this patient yet.</td>
                    </tr>
                  </tbody>
                </table>
              </section>

              <section className="help-section">
                <h3>Step 2: Configure Your Offer</h3>
                <ul>
                  <li><strong>UCPM Exit Hub</strong>: Select the hub where patients will be received in your nation.</li>
                  <li>Patients are grouped by offer type in separate containers based on your selection in Step 1.</li>
                  <li><strong>Full Offer</strong>: Your nation provides both treatment and transport.</li>
                  <li><strong>Treatment + Ext. Transport</strong>: Select the partner nation that will provide transport.</li>
                  <li><strong>Only Treatment</strong>: Transport is open / not yet secured.</li>
                  <li><strong>Provide Transport</strong>: Select which treatment nations to notify. You can deselect individual nations using the checkboxes.</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OfferCreate;
